// The messages of a conversation, as the Assistant page, the panel and the phone show them: your words on the right,
// the assistant's on the left with its orb. An answer can carry a facts table, numbered sources, follow-up questions
// and choices; anything it wants to do comes as a card to approve, in the message that proposed it.
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'motion/react';
import type { Message, ProposedAction } from '@dpw/shared';
import { listItem, spring, stagger } from '../lib/motion';
import { ApproveCard } from '../ui/ApproveCard';
import { ErrorNote } from '../ui/bits';
import { Orb, Skel } from '../ui/motion';
import { stableKey } from './useChat';

export function Thread({ messages, actions, thinking, loading, error, onAsk, phone = false, live, reply, thinkingText = 'Reading the track, minutes and updates' }: {
  messages: Message[]; actions: ProposedAction[]; thinking: boolean; loading?: boolean; error?: unknown; onAsk: (t: string) => void; phone?: boolean;
  /** What you are saying right now (voice): a bubble that fills in as you speak. */
  live?: string | null;
  /** The reply as it is being written (streamed), shown in place of the thinking dots. */
  reply?: string | null;
  thinkingText?: string;
}) {
  const byId = new Map<string, ProposedAction>(actions.map(a => [a.id, a]));
  return (
    <>
      {loading && <ThreadSkeleton />}
      <AnimatePresence initial={false}>
        {messages.map(m => (
          <motion.div key={stableKey.get(m.id) ?? m.id} className={m.role === 'user' ? 'um' : 'am'} layout="position"
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0, transition: spring.gentle }}>
            {m.role === 'user' ? m.content : <AiMessage m={m} actionsById={byId} onAsk={onAsk} orb={phone ? 22 : 14} />}
          </motion.div>
        ))}
        {live !== undefined && live !== null && (
          <motion.div key="live" className="um live" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0, transition: spring.gentle }} exit={{ opacity: 0 }}
            role="status" aria-label="What you are saying">
            {live || <span className="lvdots" aria-hidden="true"><i /><i /><i /></span>}
          </motion.div>
        )}
        {thinking && (reply
          ? <motion.div key="think" className="am" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <span className="av"><Orb size={phone ? 22 : 14} state="thinking" /></span>
            <div className="ab"><div className="tx streaming" aria-live="polite">{reply}</div></div>
          </motion.div>
          : <motion.div key="think" className="am" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <span className="av"><Orb size={phone ? 22 : 14} state="thinking" /></span>
            <div className="think" role="status">{phone ? <span className="typing"><i /><i /><i /></span> : <span className="dots"><i /><i /><i /></span>}{thinkingText}</div>
          </motion.div>)}
      </AnimatePresence>
      {error ? <ErrorNote error={error} /> : null}
    </>
  );
}

export function AiMessage({ m, actionsById, onAsk, orb = 14 }: { m: Message; actionsById: Map<string, ProposedAction>; onAsk: (t: string) => void; orb?: number }) {
  const cards = (m.meta.actionIds ?? []).map(aid => actionsById.get(aid)).filter((a): a is ProposedAction => !!a && !(m.meta.approvedIds ?? []).includes(a.id));
  return (
    <>
      <span className="av"><Orb size={orb} /></span>
      <div className="ab">
        <div className="tx">{m.content}</div>
        {!!m.meta.facts?.length && (
          <div className="facts" role="table" aria-label="Facts">
            {m.meta.facts.map((f, i) => <div className={`f${f.warn ? ' warn' : ''}`} role="row" key={i}><span className="k2" role="rowheader">{f.label}</span><span role="cell">{f.value}</span></div>)}
          </div>
        )}
        {cards.map(a => <ApproveCard key={a.id} action={a} variant="chat" composer />)}
        {!!m.meta.choices?.length && (
          <div className="chs">{m.meta.choices.map((c, i) => <motion.button key={c} type="button" className={`bt sm${i === 0 ? ' pri' : ''}`} whileTap={{ scale: 0.96 }} onClick={() => onAsk(c)}>{c}</motion.button>)}</div>
        )}
        {!!m.meta.sources?.length && (
          <motion.div className="srow" variants={{ animate: { transition: stagger(0.03) } }} initial="initial" animate="animate">
            <span className="lb">Sources</span>
            {m.meta.sources.map((s, i) => {
              // One line each: a long name ends in an ellipsis, the whole of it in the tooltip and the accessible name.
              const body = <><b>{i + 1}</b><span className="sl">{s.label}</span></>;
              const name = `Source ${i + 1}: ${s.label}`;
              return <motion.span key={i} className="srci" variants={listItem}>{s.link ? <Link className="srcc" to={s.link} title={s.label} aria-label={name}>{body}</Link> : <span className="srcc" title={s.label} aria-label={name}>{body}</span>}</motion.span>;
            })}
          </motion.div>
        )}
        {m.meta.knowledge && <div className="xs mut" style={{ marginTop: 6 }}>{m.meta.knowledge.line}</div>}
        {m.meta.nav && <Link className="navchip" to={m.meta.nav.to}><span className="k">Open</span><span className="t">{m.meta.nav.label}</span></Link>}
        {!!m.meta.followUps?.length && (
          <div className="fu">{m.meta.followUps.map(f => <motion.button key={f} type="button" className="fub" title={f} whileTap={{ scale: 0.96 }} onClick={() => onAsk(f)}><span className="ft">{f}</span></motion.button>)}</div>
        )}
      </div>
    </>
  );
}

export function ThreadSkeleton() {
  return (
    <div className="col gap12" role="status" aria-label="Loading">
      <Skel w="40%" h={38} r={18} style={{ alignSelf: 'flex-end' }} />
      <div className="rowc" style={{ alignItems: 'flex-start' }}><Skel w={30} h={30} r={15} /><div className="col gap6" style={{ flex: 1 }}><Skel w="80%" h={13} /><Skel w="65%" h={13} /><Skel h={110} r={14} /></div></div>
    </div>
  );
}
